'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';


import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';

import {
  OrganizationVo,
  OrganizationVoTypeEnum,
} from '@/lib/api/data-contracts';

import {
  AlertCircle,
  Building,
  Building2,
  Edit2,
  FuelIcon as GasStation,
  GripVertical,
  Info,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Trash2,
  Warehouse,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { NodeApi, Tree } from 'react-arborist';

// 조직 타입 정의
interface OrganizationNode {
  id: string;
  name: string;
  type: OrganizationVoTypeEnum;
  parentId?: string;
  children?: OrganizationNode[];
  // 추가 속성들
  orgCd?: string;
  orgName?: string;
  parentOrgCd?: string;
  // 주유소 전용 속성들
  address?: string;
  catmagam?: boolean;
  posdata?: boolean;
  gasmode?: string;
}

interface OrganizationFormData {
  parentOrgCd: string;
  organizationName: string;
  storeCd: string;
  type: OrganizationVoTypeEnum;
}

// 트리 노드 컴포넌트
function TreeNode({
  node,
  style,
  dragHandle,
  onCreateOrganization,
  onRenameOrganization,
  onDeleteOrganization,
}: {
  node: NodeApi<OrganizationNode>;
  style: any;
  dragHandle?: any;
  onCreateOrganization: (
    parentId: string,
    type: OrganizationVoTypeEnum
  ) => void;
  onRenameOrganization: (node: OrganizationNode) => void;
  onDeleteOrganization: (node: OrganizationNode) => void;
}) {
  const getIcon = (type: OrganizationVoTypeEnum) => {
    switch (type) {
      case 'COMPANY':
        return <Building2 className='h-4 w-4 text-blue-500' />;
      case 'ORGANIZATION':
        return <Building className='h-4 w-4 text-yellow-500' />;
      case 'BUSINESS':
        return <Warehouse className='h-4 w-4 text-red-500' />;
      case 'STORE':
        return <GasStation className='h-4 w-4 text-green-500' />;
      default:
        return <Building2 className='h-4 w-4 text-gray-500' />;
    }
  };

  const canCreateOrganization = node.data.type === 'COMPANY';
  // '미 할당 주유소'(NONE)는 화면용 가상 노드라 영업지사의 상위가 될 수 없다
  const canCreateBusiness =
    node.data.type === 'ORGANIZATION' && node.data.id !== 'NONE';
  const canCreateStore =
    node.data.type === 'ORGANIZATION' || node.data.type === 'BUSINESS';
  const canRename = node.data.type !== 'COMPANY';
  const canDelete =
    node.data.type !== 'COMPANY' &&
    (!node.children || node.children.length === 0);

  return (
    <div
      style={style}
      className={`flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded group cursor-pointer ${node.isSelected ? 'bg-blue-50 border-l-2 border-blue-500' : ''
        } ${node.data.type === 'STORE' ? 'cursor-grab active:cursor-grabbing' : ''}`}
      onClick={() => node.select()}
      ref={node.data.type === 'STORE' ? dragHandle : undefined}
    >
      <div className='flex items-center gap-1 flex-1'>
        {node.isInternal && (
          <button
            className='p-1 hover:bg-gray-200 rounded'
            onClick={e => {
              e.stopPropagation();
              node.toggle();
            }}
          >
            {node.isOpen ? '▼' : '▶'}
          </button>
        )}
        {getIcon(node.data.type)}
        <span className='text-sm'>{node.data.name}</span>
        {node.data.type === 'STORE' && (
          <div className='ml-2 opacity-50 text-xs text-gray-500'>
            <GripVertical className='h-3 w-3' />
          </div>
        )}
      </div>

      {/* 메뉴 버튼 */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className='opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-opacity'
            onClick={e => e.stopPropagation()}
          >
            <MoreHorizontal className='h-3 w-3' />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className='w-48' align='start'>
          {(canCreateOrganization || canCreateBusiness || canCreateStore) && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Plus className='mr-2 h-4 w-4' />
                <span>신규생성</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {canCreateOrganization && (
                  <DropdownMenuItem
                    onClick={() =>
                      onCreateOrganization(node.data.id, 'ORGANIZATION')
                    }
                  >
                    <Building className='mr-2 h-4 w-4' />
                    <span>지사 생성</span>
                  </DropdownMenuItem>
                )}
                {canCreateBusiness && (
                  <DropdownMenuItem
                    onClick={() =>
                      onCreateOrganization(node.data.id, 'BUSINESS')
                    }
                  >
                    <Warehouse className='mr-2 h-4 w-4' />
                    <span>영업지사 생성</span>
                  </DropdownMenuItem>
                )}
                {canCreateStore && (
                  <DropdownMenuItem
                    onClick={() => onCreateOrganization(node.data.id, 'STORE')}
                  >
                    <GasStation className='mr-2 h-4 w-4' />
                    <span>주유소 생성</span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}

          {canRename && (
            <>
              {(canCreateOrganization ||
                canCreateBusiness ||
                canCreateStore) && <DropdownMenuSeparator />}
              <DropdownMenuItem onClick={() => onRenameOrganization(node.data)}>
                <Edit2 className='mr-2 h-4 w-4' />
                <span>이름 변경</span>
              </DropdownMenuItem>
            </>
          )}

          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDeleteOrganization(node.data)}
                className='text-red-600'
              >
                <Trash2 className='mr-2 h-4 w-4' />
                <span>삭제</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

interface OrganizationManagementProps {
  onOpenTab?: (item: {
    id: string;
    title: string;
    icon: React.ComponentType<{ className?: string }>;
    category: string;
  }) => void;
}

export function OrganizationManagement({ onOpenTab }: OrganizationManagementProps = {}) {
  const { toast } = useToast();
  const [organizations, setOrganizations] = useState<OrganizationNode[]>([]);
  const [selectedNode, setSelectedNode] = useState<OrganizationNode | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createType, setCreateType] =
    useState<OrganizationVoTypeEnum>('ORGANIZATION');
  const [createParentId, setCreateParentId] = useState<string>('');
  const [editingNode, setEditingNode] = useState<OrganizationNode | null>(null);
  const [newOrgCode, setNewOrgCode] = useState('');
  const [newOrgName, setNewOrgName] = useState('');
  const [editOrgName, setEditOrgName] = useState('');

  const [orgFormData, setOrgFormData] = useState<OrganizationFormData>({
    parentOrgCd: '',
    organizationName: '',
    storeCd: '',
    type: 'ORGANIZATION',
  });

  // 조직 목록 조회
  const fetchOrganizations = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('admin/organization');
      const data = (await response.json()) as OrganizationVo[];

      // 트리 구조로 변환
      const treeData = buildTreeStructure(data);
      setOrganizations(treeData);
    } catch {
      toast({
        title: '조직 목록 조회 실패',
        description: '조직 목록을 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // 트리 구조 생성
  const buildTreeStructure = (data: OrganizationVo[]): OrganizationNode[] => {
    const nodeMap = new Map<string, OrganizationNode>();
    const roots: OrganizationNode[] = [];

    // 먼저 모든 노드를 생성
    data.forEach(item => {
      const node: OrganizationNode = {
        id: item.orgCd || '',
        name: item.orgName || '',
        type: item.type || 'ORGANIZATION',
        parentId: item.parentOrgCd || undefined,
        children: [],
        orgCd: item.orgCd,
        orgName: item.orgName,
        parentOrgCd: item.parentOrgCd,
      };
      nodeMap.set(node.id, node);
    });

    // 부모-자식 관계 설정
    nodeMap.forEach(node => {
      if (node.parentId && nodeMap.has(node.parentId)) {
        const parent = nodeMap.get(node.parentId)!;
        parent.children!.push(node);
      } else {
        roots.push(node);
      }
    });

    // 미할당 주유소 노드 추가
    const unassignedStores = data.filter(
      item => !item.parentOrgCd && item.type === 'STORE'
    );
    if (unassignedStores.length > 0) {
      const unassignedNode: OrganizationNode = {
        id: 'NONE',
        name: '미 할당 주유소',
        type: 'ORGANIZATION',
        children: unassignedStores.map(store => ({
          id: store.orgCd || '',
          name: store.orgName || '',
          type: 'STORE',
          parentId: 'NONE',
          children: [],
          orgCd: store.orgCd,
          orgName: store.orgName,
        })),
      };
      roots.push(unassignedNode);
    }

    return roots;
  };

  // 노드 선택 처리
  const handleNodeSelect = (nodes: NodeApi<OrganizationNode>[]) => {
    if (nodes.length > 0) {
      const node = nodes[0];
      setSelectedNode(node.data);
      loadNodeData(node.data);
    }
  };

  // 노드 데이터 로드
  const loadNodeData = (node: OrganizationNode) => {
    setOrgFormData({
      parentOrgCd: node.parentId || '',
      organizationName: node.name,
      storeCd: node.type === 'STORE' ? node.id : '',
      type: node.type,
    });
  };



  // 초기 데이터 로드
  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // 조직 생성 핸들러
  const handleCreateOrganization = (
    parentId: string,
    type: OrganizationVoTypeEnum
  ) => {
    setCreateParentId(parentId);
    setCreateType(type);
    setNewOrgCode('');
    setNewOrgName('');
    setCreateDialogOpen(true);
  };

  // 조직 이름 변경 핸들러
  const handleRenameOrganization = (node: OrganizationNode) => {
    setEditingNode(node);
    setEditOrgName(node.name);
    setEditDialogOpen(true);
  };

  // 조직 삭제 핸들러
  const handleDeleteOrganization = (node: OrganizationNode) => {
    setEditingNode(node);
    setDeleteDialogOpen(true);
  };

  // 새 조직 생성 API 호출
  const handleCreateSubmit = async () => {
    // 지사·영업지사는 코드를 비우면 서버가 자동 생성한다
    const codeRequired = createType === 'STORE';
    if ((codeRequired && !newOrgCode.trim()) || !newOrgName.trim()) {
      toast({
        title: '입력 오류',
        description: codeRequired
          ? '주유소 코드와 주유소명을 모두 입력해주세요'
          : '조직명을 입력해주세요',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.post('admin/organization', {
        json: {
          orgCd: newOrgCode.trim() || undefined,
          orgName: newOrgName,
          parentOrgCd: createParentId === 'NONE' ? '' : createParentId,
          type: createType,
        },
      });

      toast({
        title: '생성 완료',
        description: `새 ${createType === 'ORGANIZATION' ? '지사' : createType === 'BUSINESS' ? '영업지사' : '주유소'}가 생성되었습니다`,
      });

      setCreateDialogOpen(false);
      fetchOrganizations();
    } catch (error) {
      toast({
        title: '생성 실패',
        description:
          error instanceof Error
            ? error.message
            : '조직 생성 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // 조직 이름 변경 API 호출
  const handleRenameSubmit = async () => {
    if (!editingNode || !editOrgName.trim()) {
      toast({
        title: '입력 오류',
        description: '조직명을 입력해주세요',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.patch(`admin/organization/${editingNode.id}/rename`, {
        json: { orgName: editOrgName },
      });

      toast({
        title: '수정 완료',
        description: '조직명이 성공적으로 변경되었습니다',
      });

      setEditDialogOpen(false);
      fetchOrganizations();
    } catch {
      toast({
        title: '수정 실패',
        description: '조직명 변경 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // 조직 삭제 API 호출
  const handleDeleteSubmit = async () => {
    if (!editingNode) return;

    setIsSubmitting(true);
    try {
      await apiClient.delete(`admin/organization/${editingNode.id}`);

      toast({
        title: '삭제 완료',
        description: '조직이 성공적으로 삭제되었습니다',
      });

      setDeleteDialogOpen(false);
      fetchOrganizations();
    } catch {
      toast({
        title: '삭제 실패',
        description: '조직 삭제 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // 트리에서 노드 찾기 헬퍼 함수
  const findNodeById = useCallback(
    (nodes: OrganizationNode[], id: string): OrganizationNode | null => {
      for (const node of nodes) {
        if (node.id === id) {
          return node;
        }
        if (node.children) {
          const found = findNodeById(node.children, id);
          if (found) return found;
        }
      }
      return null;
    },
    []
  );

  // 드래그 앤 드롭 핸들러
  const handleMove = useCallback(
    async (args: {
      dragIds: string[];
      parentId: string | null;
      index: number;
    }) => {
      const { dragIds, parentId } = args;

      if (dragIds.length === 0) return;

      const draggedNodeId = dragIds[0];
      const newParentId = parentId || '';

      // 드래그된 노드가 STORE 타입인지 확인
      const draggedNode = findNodeById(organizations, draggedNodeId);
      if (!draggedNode || draggedNode.type !== 'STORE') {
        toast({
          title: '이동 불가',
          description: '주유소만 드래그 앤 드롭으로 이동할 수 있습니다.',
          variant: 'destructive',
        });
        return;
      }

      // 새 부모가 ORGANIZATION 또는 BUSINESS 타입인지 확인
      if (newParentId && newParentId !== 'NONE') {
        const newParentNode = findNodeById(organizations, newParentId);
        if (
          !newParentNode ||
          (newParentNode.type !== 'ORGANIZATION' &&
            newParentNode.type !== 'BUSINESS')
        ) {
          toast({
            title: '이동 불가',
            description: '주유소는 지사 또는 영업지사에만 소속될 수 있습니다.',
            variant: 'destructive',
          });
          return;
        }
      }

      try {
        await apiClient.put(
          `admin/organization/${draggedNodeId}/parent/${newParentId}`,
          {}
        );

        toast({
          title: '이동 완료',
          description: '주유소가 성공적으로 이동되었습니다.',
        });

        fetchOrganizations();
      } catch {
        toast({
          title: '이동 실패',
          description: '주유소 이동 중 오류가 발생했습니다.',
          variant: 'destructive',
        });
      }
    },
    [organizations, toast, fetchOrganizations, findNodeById]
  );

  return (
    <div className='p-3 space-y-3'>
      {/* 브레드크럼 */}
      <Breadcrumb items={menuBreadcrumbItems('organization-management')} />

      <div className='bg-blue-50 border border-blue-200 rounded-lg p-3'>
        <div className='flex items-center text-blue-600'>
          <Info className='h-4 w-4 mr-2' />
          <span className='text-sm'>
            조직 우측에 <strong>[...]</strong> 버튼을 눌러 조직{' '}
            <strong>생성</strong> 또는 <strong>제거</strong>,{' '}
            <strong>이름변경</strong>이 가능합니다. 주유소를{' '}
            <strong>드래그 & 드롭</strong>하여 소속을 변경할 수 있습니다.
          </span>
        </div>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* 조직도 트리 */}
        <Card>
          <CardHeader>
            <div className='flex items-center justify-between'>
              <CardTitle className='flex items-center gap-2'>
                <Building2 className='h-4 w-4' />
                조직도
              </CardTitle>
              <Button
                size='icon'
                onClick={fetchOrganizations}
                disabled={isLoading}
              >
                <RefreshCw
                  className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
                />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className='h-96 border rounded-lg'>
              {organizations.length > 0 ? (
                <Tree
                  data={organizations}
                  onSelect={handleNodeSelect}
                  onMove={handleMove}
                  width='100%'
                  height={384}
                  indent={24}
                  rowHeight={32}
                >
                  {props => (
                    <TreeNode
                      {...props}
                      onCreateOrganization={handleCreateOrganization}
                      onRenameOrganization={handleRenameOrganization}
                      onDeleteOrganization={handleDeleteOrganization}
                    />
                  )}
                </Tree>
              ) : (
                <div className='flex items-center justify-center h-full text-gray-500'>
                  {isLoading ? '로딩 중...' : '조직 데이터가 없습니다'}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 조직 정보 */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Building className='h-4 w-4' />
              조직 정보
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-4'>
              <div className='grid grid-cols-12 gap-4 items-center'>
                <Label className='col-span-3'>상위조직</Label>
                <Input
                  value={orgFormData.parentOrgCd}
                  readOnly
                  className='col-span-9 bg-gray-50'
                />
              </div>

              <div className='grid grid-cols-12 gap-4 items-center'>
                <Label className='col-span-3'>조직명</Label>
                <Input
                  value={orgFormData.organizationName}
                  readOnly
                  className='col-span-9 bg-gray-50'
                />
              </div>

              {selectedNode?.type === 'STORE' && (
                <div className='grid grid-cols-12 gap-4 items-center'>
                  <Label className='col-span-3'>주유소 코드</Label>
                  <Input
                    value={orgFormData.storeCd}
                    readOnly
                    className='col-span-9 bg-gray-50'
                  />
                </div>
              )}

              <div className='grid grid-cols-12 gap-4 items-center'>
                <Label className='col-span-3'>조직구분</Label>
                <Input
                  value={
                    orgFormData.type === 'ORGANIZATION'
                      ? '지사'
                      : orgFormData.type === 'BUSINESS'
                        ? '영업지사'
                        : orgFormData.type === 'STORE'
                          ? '주유소/충전소'
                          : '알 수 없음'
                  }
                  readOnly
                  className='col-span-9 bg-gray-50'
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 주유소 정보 안내 (주유소 선택 시에만 표시) */}
      {selectedNode?.type === 'STORE' && (
        <Card className='mt-6'>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <GasStation className='h-4 w-4' />
              주유소 상세 정보
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-blue-800 font-medium'>
                    주유소 상세 정보 수정이 필요하신가요?
                  </p>
                  <p className='text-xs text-blue-600 mt-1'>
                    주유소 정보 관리 페이지에서 전화번호, 주소, 사업자번호 등의 상세 정보를 수정할 수 있습니다.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className='ml-4'
                  onClick={() => {
                    if (onOpenTab) {
                      onOpenTab({
                        id: 'station-management',
                        title: '주유소 정보 관리',
                        icon: GasStation,
                        category: '관리',
                      });
                    }
                  }}
                >
                  주유소 정보 관리로 이동
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 조직 생성 다이얼로그 */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>
              {createType === 'ORGANIZATION'
                ? '지사'
                : createType === 'BUSINESS'
                  ? '영업지사'
                  : '주유소'}{' '}
              생성
            </DialogTitle>
          </DialogHeader>
          <div className='space-y-4'>
            <div>
              <Label htmlFor='orgCode'>조직 코드</Label>
              <Input
                id='orgCode'
                value={newOrgCode}
                onChange={e => setNewOrgCode(e.target.value)}
                placeholder={
                  createType === 'STORE'
                    ? '주유소 코드를 입력하세요'
                    : '비워두면 자동 생성됩니다'
                }
              />
            </div>
            <div>
              <Label htmlFor='orgName'>조직 명</Label>
              <Input
                id='orgName'
                value={newOrgName}
                onChange={e => setNewOrgName(e.target.value)}
                placeholder='조직 명을 입력하세요'
              />
            </div>
            <div className='flex justify-end gap-2'>
              <Button
                variant='outline'
                onClick={() => setCreateDialogOpen(false)}
                disabled={isSubmitting}
              >
                취소
              </Button>
              <Button onClick={handleCreateSubmit} disabled={isSubmitting}>
                {isSubmitting ? '생성 중...' : '생성'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 조직 이름 변경 다이얼로그 */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>조직 이름 변경</DialogTitle>
          </DialogHeader>
          <div className='space-y-4'>
            <div>
              <Label htmlFor='editOrgName'>조직 명</Label>
              <Input
                id='editOrgName'
                value={editOrgName}
                onChange={e => setEditOrgName(e.target.value)}
                placeholder='새 조직 명을 입력하세요'
              />
            </div>
            <div className='flex justify-end gap-2'>
              <Button
                variant='outline'
                onClick={() => setEditDialogOpen(false)}
                disabled={isSubmitting}
              >
                취소
              </Button>
              <Button onClick={handleRenameSubmit} disabled={isSubmitting}>
                {isSubmitting ? '변경 중...' : '변경'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 조직 삭제 확인 다이얼로그 */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>조직 삭제 확인</DialogTitle>
          </DialogHeader>
          <div className='space-y-4'>
            <div className='flex items-center gap-2 text-amber-600'>
              <AlertCircle className='h-5 w-5' />
              <span>정말로 이 조직을 삭제하시겠습니까?</span>
            </div>
            <div className='bg-gray-50 p-3 rounded'>
              <p className='text-sm text-gray-600'>
                조직명: <strong>{editingNode?.name}</strong>
              </p>
              <p className='text-sm text-gray-600'>
                조직코드: <strong>{editingNode?.id}</strong>
              </p>
            </div>
            <div className='flex justify-end gap-2'>
              <Button
                variant='outline'
                onClick={() => setDeleteDialogOpen(false)}
                disabled={isSubmitting}
              >
                취소
              </Button>
              <Button
                variant='destructive'
                onClick={handleDeleteSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? '삭제 중...' : '삭제'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


    </div>
  );
}
